import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
// Constants
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
// Icons
import { Check, Eye, EyeOff } from "lucide-react";
// Hooks
import useForm from "../hooks/useForm";
import { useThunk } from "../hooks/useThunk";
// Thunks
import {
  createUser,
  getUserProfile,
  updateUser,
} from "../store/thunks/userThunks";
// Utils
import showToast from "../utils/toastNotifications";
//Components
import CustomInput from "../components/Form/Input";
import CustomDropDown from "../components/Form/DropDownNew";
import CustomButton from "../components/Form/CustomButton";

const CreateUser = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = searchParams.get("mode"); // view or edit
  const userId = searchParams.get("id"); // the ID from ViewUsers

  const [checked, setChecked] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [stillSending, setStillSending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [runCreateUser] = useThunk(createUser);
  const [fetchUser] = useThunk(getUserProfile);
  const [runUpdateUser] = useThunk(updateUser);

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  const initialValues = {
    fullName: "",
    email: "",
    roles: "Staff",
    password: "",
  };

  const requiredFields = {
    fullName: true,
    email: true,
    roles: true,
    password: true,
  };

  const inputTypes = {
    fullName: { type: COMMON_FIELD_TYPES.FULLNAME },
    email: { type: COMMON_FIELD_TYPES.EMAIL_FIELD },
    roles: { type: COMMON_FIELD_TYPES.DROPDOWN },
    password: { type: COMMON_FIELD_TYPES.PASSWORD_FIELD },
  };

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    clearError,
    validateForm,
    setFormData,
    setErrors,
    setTouched,
  } = useForm(initialValues, requiredFields, inputTypes);

  // Only send staff or family member for the option list of dropdown
  const roleOptions = [
    { name: "STAFF", id: "STAFF" },
    { name: "FAMILY MEMBER", id: "FAMILY MEMBER" },
  ];
  const allRoleOptions = [
    { name: "STAFF", id: "STAFF" },
    { name: "FAMILY MEMBER", id: "FAMILY MEMBER" },
  ];
  // Ensure the user selected role is staff or family member
  const selectedOption =
    allRoleOptions.find(
      (opt) => opt.name.toLowerCase() === formData.roles?.toLowerCase()
    ) || null;

  useEffect(() => {
    if (isCreateMode) {
      setFormData(initialValues);
      setOriginalData(null);
    }
  }, [mode]);

  // Fetch user data for view or edit
  useEffect(() => {
    const loadUser = async () => {
      if (userId) {
        const { success, response, error } = await fetchUser(userId);

        if (success && response) {
          const user = response.data.user;

          const loadedData = {
            fullName: `${user.firstName} ${user.lastName}` || "",
            email: user.email || "",
            roles: user.roles || "Staff",
            password: "",
          };

          setFormData(loadedData);

          setOriginalData(loadedData);
        } else {
          showToast("error", error.message || "Failed to load user details");
        }
      }
    };

    loadUser();
  }, [userId]);

  // Helper function to find updated fields
  const getUpdatedFields = (original, updated) => {
    if (!original) return updated; // no original means create, send all

    const changedFields = {};
    for (const key in updated) {
      if (updated[key] !== original[key]) {
        changedFields[key] = updated[key];
      }
    }
    return changedFields;
  };

  // Helper function to split fullName into first and last names
  const splitFullName = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const [firstName, ...lastNames] = fullName.trim().split(" ");
    return { firstName, lastName: lastNames.join(" ") };
  };

  const isEditNoChanges =
    isEditMode &&
    originalData &&
    Object.keys(getUpdatedFields(originalData, formData)).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (stillSending) return; //prevent duplicate submission if the submit button double clicked
    setStillSending(true);

    if (mode === "edit" && userId) {
      const updatedFields = getUpdatedFields(originalData, formData);
      // If there is no updated fields skip the API call
      if (Object.keys(updatedFields).length === 0) {
        setStillSending(false); // Fix: Set stillSending to false before returning
        return;
      }

      if (updatedFields.fullName) {
        const { firstName, lastName } = splitFullName(updatedFields.fullName);
        updatedFields.firstName = firstName;
        updatedFields.lastName = lastName;
        delete updatedFields.fullName;
      }

      // Add email notification preference for updates if needed
      updatedFields.sendEmail = checked;

      const { success, error } = await runUpdateUser({
        id: userId,
        updatedFields,
      });

      if (success) {
        showToast("success", "User updated successfully.");
        handleCancel();
        navigate("/admin");
      } else {
        showToast("error", error?.message || "Something went wrong.");
      }
      setStillSending(false);
    } else {
      if (!validateForm()) {
        showToast("error", "Please fill in all required fields.");
        setStillSending(false);
        return;
      }
      const { firstName, lastName } = splitFullName(formData.fullName);

      const createData = {
        ...formData,
        firstName,
        lastName,
        sendEmail: checked,
        roles: formData.roles.toLowerCase(), // Normalize roles to lowercase
      };
      delete createData.fullName;

      const { success, error } = await runCreateUser(createData);

      if (success) {
        showToast("success", "User created successfully.");
        handleCancel();
        navigate("/admin");
      } else {
        showToast("error", error.message || "Something went wrong.");
      }
      setStillSending(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && originalData) {
      setFormData(originalData);
    } else {
      setFormData(initialValues);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center items-center sm:shadow-[4px_4px_32px_rgba(36,132,147,0.2)] rounded-2xl w-[568px] ml:w-[600px] lg:w-[824px] xl2:w-[1100px] py-10 mt-[25px] sm:my-[50px] font-poppins"
      >
        <div className="flex flex-col items-start w-[280px] xs:w-[335px] sm:w-[480px] lg:w-[650px] xl2:w-[700px]">
          <p className="w-full text-center font-inter font-medium text-level-5 ml:text-lg lg:text-level-3 xl2:text-[26px] mb-10">
            {isCreateMode &&
              "Add a new staff member or a family member to the system"}
            {isEditMode && "Edit User Details"}
            {isViewMode && "View User Details"}
          </p>

          <div className="space-y-5">
            <CustomInput
              label="Full Name"
              placeholder="Enter your full name"
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
              onBlur={(e) => handleBlur("fullName", e.target.value)}
              onFocus={() => clearError("fullName")}
              isRequired={!isViewMode}
              disabled={isViewMode}
              labelStyle="text-level-6 font-medium text-[#344054] font-inter"
              inputStyle="h-[40px] w-[280px] xs:w-[335px] sm:w-[480px] lg:w-[650px] xl2:w-[700px] border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
              error={touched.fullName && errors.fullName}
            />

            <CustomInput
              label="Email"
              placeholder="Enter email address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={(e) => handleBlur("email", e.target.value)}
              onFocus={() => clearError("email")}
              isRequired={!isViewMode}
              disabled={isViewMode}
              labelStyle="text-level-6 font-medium text-[#344054] font-inter"
              inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
              error={touched.email && errors.email}
            />
            {!isViewMode && (
              <CustomDropDown
                label="User Role"
                options={roleOptions}
                defaultOption={selectedOption}
                idKey="id"
                onSelect={(selected) => {
                  handleInputChange("roles", selected.name);
                }}
                disabled={isViewMode}
                labelStyle="text-level-6 font-medium text-[#344054] font-inter"
                buttonClassName="h-[40px] ml:w-[400px] border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] !text-[#334155] px-4 cursor-pointer text-left text-level-6 xl:text-level-5 font-normal"
                dropdownClassName="ml:w-[400px] rounded-xl border border-[#E6E7EC] bg-[#E0F2F1]/30 shadow-lg text-left text-level-6 xl:text-level-5 font-normal py-0"
                optionClassName="text-[#334155]"
                isRequired={true}
                name="roles"
                allowFillColor={true}
              />
            )}
            {isViewMode && (
              <CustomInput
                label="User Role"
                value={formData.roles}
                disabled
                labelStyle="text-level-6 font-medium text-[#344054] font-inter"
                inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
              />
            )}
            {isCreateMode && (
              <div className="relative">
                <CustomInput
                  label={isEditMode ? "New Password (optional)" : "Password"}
                  placeholder={
                    isEditMode
                      ? "Enter new password (leave empty to keep current)"
                      : "Set a temporary password"
                  }
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  onBlur={(e) => handleBlur("password", e.target.value)}
                  onFocus={() => clearError("password")}
                  isRequired={isCreateMode} // Only required for create mode
                  disabled={isViewMode}
                  labelStyle="text-level-6 font-medium text-[#344054] font-inter"
                  inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
                  error={touched.password && errors.password}
                />
                <button
                  type="button"
                  className="absolute right-4 top-[50px] -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}
          </div>

          {isViewMode ||
            (!isEditMode && (
              <div className="flex justify-start mt-5">
                <label className="flex text-level-7 xs:text-level-6 font-medium text-[#344054] sm:gap-3 font-inter items-center">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setChecked(!checked)}
                    className="hidden"
                  />
                  <div className="w-[20px] h-[20px] border border-[#344054] rounded-sm flex items-center justify-center">
                    {checked && (
                      <Check className="w-4 h-4 text-red-500" strokeWidth={3} />
                    )}
                  </div>
                  Send credentials via email to the new user
                </label>
              </div>
            ))}
        </div>
        {!isViewMode && (
          <div className="text-level-6 xl:text-[18px] font-semibold flex gap-[30px] font-inter mt-5">
            <CustomButton
              type="button"
              className="border-1 border-[#2B7C86] h-[30px] ml:h-[40px] xl:h-[55px] w-[100px] ml:w-[150px] xl:w-[220px] rounded-lg text-[#2B7C86] "
              buttonText="Cancel"
              onClick={handleCancel}
            />
            <CustomButton
              type="submit"
              disabled={isViewMode || isEditNoChanges}
              loading={stillSending}
              className={`border-1 border-[#2B7C86] h-[30px] ml:h-[40px] xl:h-[55px] w-[100px] ml:w-[150px] xl:w-[220px] rounded-lg bg-[#2B7C86] text-white ${
                isViewMode || isEditNoChanges
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              buttonText={isEditMode ? "Update User" : "Create User"}
              onClick={handleSubmit}
            />
          </div>
        )}
        {isViewMode && (
          <CustomButton
            type="button"
            className={`border-1 border-[#2B7C86] px-5 h-[35px] ml:h-[40px] xl:h-[55px] w-[150px] ml:w-[150px] xl:w-[220px] rounded-lg bg-[#2B7C86] text-white mt-10`}
            buttonText="Back"
            onClick={() => navigate("/admin")}
          />
        )}
      </form>
    </div>
  );
};
export default CreateUser;
